import React, { useMemo, useState, useEffect } from 'react'
import { OptionsState } from 'particles/Dropdown'
import Loader from 'components/Shared/Loader'
import { useSelector, useDispatch } from 'react-redux'
import { FilterItem } from '../FilterAndSortBar'
import { selectParentChildLocationData, setParentChildLocationData } from 'state/review'
import { LocationOption } from 'services/api/constants'
import MenuItem from 'particles/MenuItem'

interface LocationFilterDropdownProps {
    options: OptionsState[] | null
    selectedOptions: FilterItem[]
    onSelectOption: (options: OptionsState[], isChildOption?: boolean, parent?: OptionsState) => void
    childOptions?: LocationOption[]
    parentId: string
    field: string
    onLoadChildren?: (parentId: string) => void
    isLoading?: boolean
}

const LocationFilterDropdownList: React.FC<LocationFilterDropdownProps> = ({
    options,
    selectedOptions,
    onSelectOption,
    childOptions,
    parentId,
    field,
    onLoadChildren,
    isLoading,
}) => {
    if (!options || options.length === 0) return null

    const dispatch = useDispatch()

    const parentChildLocationData = useSelector(selectParentChildLocationData)

    const [toggleChildren, setToggleChildren] = useState('')
    const [prevSelected, setPrevSelected] = useState<string[]>([])
    const [isParentSelected, setIsParentSelected] = useState<boolean>(false)

    const isSelected = (value: string) => selectedOptions.some(option => option.value === value)

    const selectedParent = isSelected(parentId)

    const toggleChildOptions = (e: React.MouseEvent<HTMLElement>, option: OptionsState): void => {
        e.preventDefault()
        if (toggleChildren === option.value) {
            setToggleChildren('')
            return
        }
        const parentId = option.value
        setToggleChildren(option.value)
        onLoadChildren && onLoadChildren(parentId)
    }

    const loadChildrenOnSelectOption = () => {
        onLoadChildren && onLoadChildren(parentId)
    }

    const children = useMemo(() => {
        return childOptions?.map((option: { name: string; id: number }) => ({
            label: option.name,
            value: `{"rank1":"","rank2":"${option.id}"}`,
        }))
    }, [childOptions])

    useEffect(() => {
        if (children)
            dispatch(
                setParentChildLocationData({
                    ...parentChildLocationData,
                    [parentId]: children,
                }),
            )
    }, [children, parentId, dispatch])

    const checkIndeterminate = useMemo(() => {
        return (filteredOptions: FilterItem[], children: OptionsState[], parent: OptionsState) => {
            const filteredValues = filteredOptions?.map((item: FilterItem) => item.value)
            const childValues = children?.map(item => item.value)

            const allExist = childValues?.every(value => filteredValues.includes(value))
            const noneExist = childValues?.every(value => !filteredValues.includes(value))
            const someExist = childValues?.some(value => filteredValues.includes(value))

            const parentExists = filteredOptions?.some(option => option.value === parent.value)
            if (parentExists) return false

            if (allExist) {
                parent && !isParentSelected && onSelectOption([parent])
                return false
            } else if (noneExist) {
                return false
            } else if (someExist) {
                return true
            } else {
                return false // Handle edge cases
            }
        }
    }, [selectedOptions, children, parent])

    useEffect(() => {
        if (selectedOptions && children && field === 'location') {
            const childOptions: OptionsState[] = []
            children.forEach(childOption => {
                const isChildOption = selectedOptions?.some(
                    option => option.value === childOption.value && option.label === childOption.label,
                )

                // To make sure all children are deselected if parent is deselected
                if (isChildOption && !isSelected(parentId) && isParentSelected) {
                    childOptions.push(childOption)
                }

                if (!isChildOption && isSelected(parentId) && isParentSelected) {
                    childOptions.push(childOption)
                }
            })
            onSelectOption(childOptions)
            setPrevSelected([...prevSelected, parentId])
            loadChildrenOnSelectOption()
        }
    }, [childOptions, selectedParent])

    return (
        <>
            {options?.length &&
                options.map((item: OptionsState, i) => {
                    const isSelected = selectedOptions.some(
                        option => option.value === item.value && option.label === item.label,
                    )
                    const children = parentChildLocationData[item.value]

                    return (
                        <MenuItem.CheckboxAccordion
                            key={item.value}
                            checkbox={
                                isSelected
                                    ? 'selected'
                                    : checkIndeterminate(selectedOptions, children, item)
                                      ? 'indeterminate'
                                      : 'notSelected'
                            }
                            onChange={() => {
                                setIsParentSelected(true)
                                onSelectOption([item])
                                setToggleChildren(item.value)
                            }}
                            onHeaderClick={e => {
                                setIsParentSelected(false)
                                toggleChildOptions(e, item)
                            }}
                            label={item.label}
                        >
                            {isLoading ? (
                                <Loader size="s" className="loader" />
                            ) : (
                                children?.length &&
                                children.map((childItem: OptionsState) => {
                                    const isChildSelected = selectedOptions.some(
                                        option => option.value === childItem.value && option.label === childItem.label,
                                    )
                                    return (
                                        <MenuItem.Checkbox
                                            key={childItem.value}
                                            text={childItem.label}
                                            selected={isChildSelected}
                                            onClick={() => {
                                                onSelectOption([childItem], true, item)
                                                setIsParentSelected(false)
                                            }}
                                        />
                                    )
                                })
                            )}
                        </MenuItem.CheckboxAccordion>
                    )
                })}
        </>
    )
}

export default LocationFilterDropdownList
